import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ShieldAlert, Loader2, CheckCircle2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const money = (v) => `$${(parseFloat(v) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const ADDISON_ID = "cd22547e-6f16-4e08-942d-fd999fb85f2e";
const HAMPTON_ID = "ad3d3d95-7da8-4b0c-a4aa-57031a873596";
const PATHS = {
  addison: { preview: "addison-zero-balance-fix/preview", apply: "addison-zero-balance-fix/apply" },
  hampton: { preview: "hampton-timeline-fix/preview", apply: "hampton-timeline-fix/apply" },
};

function Row({ label, before, after }) {
  return (
    <div className="grid grid-cols-3 gap-2 text-xs py-1 border-b border-white/5 last:border-0">
      <span className="text-white/50">{label}</span>
      <span className="text-white/70">{before}</span>
      <span className="text-[#F2B705] font-medium">{after}</span>
    </div>
  );
}

// Owner-only, narrowly scoped preview/confirm/apply control for the exact Addison
// zero-balance correction and the exact Hampton $0 timeline fix — hardcoded to these two
// booking IDs on the backend, this card only ever renders for them.
export default function ReconciliationFixCard({ bookingId, token, onApplied }) {
  const kind = bookingId === ADDISON_ID ? "addison" : bookingId === HAMPTON_ID ? "hampton" : null;
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [pwd, setPwd] = useState("");
  const [applied, setApplied] = useState(null);
  const auth = { headers: { Authorization: `Bearer ${token}` } };

  if (!kind) return null;

  const loadPreview = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/bookings/${PATHS[kind].preview}`, auth);
      setPreview(data);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not load the correction preview."); }
    finally { setLoading(false); }
  };

  const apply = async () => {
    if (kind === "addison" && !pwd) { toast.error("Enter the management password to confirm."); return; }
    setApplying(true);
    try {
      const body = kind === "addison" ? { management_password: pwd } : {};
      const { data } = await axios.post(`${API}/bookings/${PATHS[kind].apply}`, body, auth);
      setApplied(data);
      setPwd("");
      toast.success(data.already_applied ? "Already applied — no changes made." : "Owner-approved correction applied.");
      onApplied?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not apply the correction."); }
    finally { setApplying(false); }
  };

  return (
    <div data-testid={`reconciliation-fix-card-${bookingId}`}
      className="rounded-lg border border-amber-500/30 bg-[#0C0C0C] p-4 space-y-3">
      <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-amber-400">
        <ShieldAlert size={14} /> Owner-Approved Correction ({kind === "addison" ? "Rochelle Addison — Zero Balance" : "Martrell Hampton — Timeline Gap"})
      </div>

      {!preview && (
        <button data-testid={`reconciliation-fix-preview-btn-${bookingId}`} onClick={loadPreview} disabled={loading}
          className="text-xs font-semibold border border-amber-500/40 text-amber-300 px-3 py-1.5 rounded hover:bg-amber-500 hover:text-black transition-colors disabled:opacity-50">
          {loading ? <Loader2 className="animate-spin inline" size={12} /> : "Preview Correction"}
        </button>
      )}

      {preview && kind === "addison" && (
        <div data-testid={`reconciliation-fix-preview-${bookingId}`} className="space-y-2">
          {preview.already_applied || applied?.ok ? (
            <div className="flex items-center gap-2 text-emerald-400 text-xs"><CheckCircle2 size={13} /> Already applied — displayed balance and credit are $0.00.</div>
          ) : (
            <>
              <div className="grid grid-cols-3 gap-2 text-[10px] uppercase tracking-wider text-white/40 pb-1 border-b border-white/10">
                <span>Field</span><span>Before</span><span>After</span>
              </div>
              <Row label="Total Charges" before={money(preview.before.total_price)} after={money(preview.after.total_price)} />
              <Row label="Amount Paid" before={money(preview.before.amount_paid)} after={money(preview.after.amount_paid)} />
              <Row label="08/02/26 Payment Method" before={preview.before.payment_2026_08_02_type || "—"} after="cash_app" />
              <Row label="Displayed Balance" before={money(preview.before.displayed_balance)} after={money(preview.after.displayed_balance)} />
              <Row label="Displayed Credit" before={money(preview.before.displayed_credit)} after={money(preview.after.displayed_credit)} />
              <Row label="Overpayment Waived" before="—" after={money(preview.after.waived_credit_amount)} />
              <p className="text-[11px] text-white/40">{preview.note}</p>
              <div className="flex items-center gap-2 pt-1">
                <input data-testid={`reconciliation-fix-password-${bookingId}`} type="password" value={pwd}
                  onChange={(e) => setPwd(e.target.value)} placeholder="Management password"
                  className="bg-black/40 border border-white/15 rounded px-2 py-1 text-xs text-white flex-1" />
                <button data-testid={`reconciliation-fix-apply-btn-${bookingId}`} onClick={apply} disabled={applying}
                  className="text-xs font-semibold border border-emerald-500/40 text-emerald-300 px-3 py-1.5 rounded hover:bg-emerald-500 hover:text-black transition-colors disabled:opacity-50 whitespace-nowrap">
                  {applying ? <Loader2 className="animate-spin inline" size={12} /> : "Confirm & Apply"}
                </button>
              </div>
            </>
          )}
        </div>
      )}

      {preview && kind === "hampton" && (
        <div data-testid={`reconciliation-fix-preview-${bookingId}`} className="space-y-2">
          {preview.already_covered || applied?.ok ? (
            <div className="flex items-center gap-2 text-emerald-400 text-xs"><CheckCircle2 size={13} /> Timeline already covered through {preview.covered_through || HAMPTON_TARGET} — no gap to close.</div>
          ) : (
            <>
              <div className="grid grid-cols-3 gap-2 text-[10px] uppercase tracking-wider text-white/40 pb-1 border-b border-white/10">
                <span>Field</span><span>Before</span><span>After</span>
              </div>
              <Row label="Coverage Gap" before={preview.covered_through} after={preview.proposed_new_record?.new_return_date} />
              <Row label="Gap Days ($0)" before="—" after={`${preview.proposed_new_record?.days} day(s)`} />
              <Row label="Return Date" before={preview.booking_field_changes?.return_date?.before} after={preview.booking_field_changes?.return_date?.after} />
              <Row label="Total Price" before="unchanged" after="unchanged" />
              <button data-testid={`reconciliation-fix-apply-btn-${bookingId}`} onClick={apply} disabled={applying}
                className="text-xs font-semibold border border-emerald-500/40 text-emerald-300 px-3 py-1.5 rounded hover:bg-emerald-500 hover:text-black transition-colors disabled:opacity-50">
                {applying ? <Loader2 className="animate-spin inline" size={12} /> : "Confirm & Apply"}
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}

const HAMPTON_TARGET = "2026-09-18";
